import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Icons } from "../components/Icons";
import FileViewer from "../components/FileViewer";
import DocumentCard from "../components/DocumentCard";
import { CardsGridSkeleton } from "../components/SkeletonLoader";
import { BASE_URL, getMyOrganization } from "../services/api";

// Older deployed backends only expose per-category endpoints that return document
// names (no ids / template file paths). Fall back to them so the page still works
// while the backend is being updated. Cards render with the paper-silhouette fallback.
const fetchFallbackDocuments = async () => {
  const categories = ["accreditation", "reaccreditation", "offcampus"];
  const results = [];
  for (const category of categories) {
    try {
      const res = await axios.get(`${BASE_URL}/checklist/documents/${category}`);
      const names = (res.data && res.data.documents) || [];
      names.forEach((name, index) => {
        results.push({
          required_document_id: `${category}-${index}`,
          document_name: name,
          template_file_path: "",
          category,
        });
      });
    } catch {
      // skip category if the endpoint is unavailable
    }
  }
  return results;
};

const OrgRequirements = () => {
  const [accreditationReqs, setAccreditationReqs] = useState([]);
  const [reaccreditationReqs, setReaccreditationReqs] = useState([]);
  const [offCampusReqs, setOffCampusReqs] = useState([]);
  const [orgType, setOrgType] = useState(null); // 'new' or 'existing'
  const [loadingReqs, setLoadingReqs] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoadingReqs(true);
        setFetchError(null);

        // Primary: dedicated no-auth endpoint that returns all categories as a flat list.
        // Fallback: older backends without that endpoint (returns names only).
        let all;
        try {
          const docsRes = await axios.get(`${BASE_URL}/checklist/all-templates`);
          all = docsRes.data;
          setUsingFallback(false);
        } catch {
          setUsingFallback(true);
          all = await fetchFallbackDocuments();
        }
        setAccreditationReqs(all.filter((d) => d.category === "accreditation"));
        setReaccreditationReqs(all.filter((d => d.category === "reaccreditation")));
        setOffCampusReqs(all.filter((d) => d.category === "offcampus"));

        // Fetch org type separately — if this fails (e.g. user not linked to org yet),
        // default to 'new' so accreditation requirements are shown
        try {
          const orgRes = await getMyOrganization();
          setOrgType(orgRes.data.org_type || "new");
        } catch {
          setOrgType("new");
        }
      } catch (error) {
        console.error("Failed to fetch requirements:", error);
        const detail = error?.response?.data?.detail || error?.message || "Unknown error";
        setFetchError(`Failed to load requirements: ${detail}`);
      } finally {
        setLoadingReqs(false);
      }
    };
    fetchAll();
  }, []);

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();
  const isOffCampusUser = user?.application_type === "offcampus";

  // Determine which accreditation list to show based on org type
  // 'new' → Accreditation, 'existing' → Re-accreditation
  const primaryReqs = orgType === "existing" ? reaccreditationReqs : accreditationReqs;
  const primaryLabel = orgType === "existing" ? "Re-accreditation Requirements" : "Accreditation Requirements";
  const primarySublabel = orgType === "existing"
    ? "Document templates for existing organizations"
    : "Document templates for new organizations seeking accreditation";

  return (
    <div className="flex flex-col h-full bg-[#f8f9fa] relative">
      <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 sm:py-8 custom-scrollbar">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Requirements</h2>

        {usingFallback && (
          <p className="text-xs text-gray-500 mb-6">
            Some document previews are temporarily unavailable.
          </p>
        )}

        {loadingReqs ? (
          <CardsGridSkeleton count={8} />
        ) : fetchError ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-3">
            <p className="text-red-500 font-medium">{fetchError}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#086C53] text-white text-sm rounded-lg hover:bg-[#065440] transition-colors"
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-8 max-w-6xl">
            {/* Accreditation OR Re-accreditation — shown ONLY for Accreditation users */}
            {!isOffCampusUser && (
              <div className="bg-white rounded-[16px] sm:rounded-[20px] shadow-sm border border-gray-200 p-4 sm:p-6 overflow-hidden">
                <div className="flex items-start sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-gray-900 font-bold text-base sm:text-lg">
                      {primaryLabel}
                    </h3>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {primarySublabel}
                    </p>
                  </div>
                  <span className="bg-[#086C53]/10 text-[#086C53] text-[11px] sm:text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0 self-start sm:self-auto">
                    {primaryReqs.length} Documents
                  </span>
                </div>

                {primaryReqs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-12">
                    No requirements posted yet.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {primaryReqs.map((req, index) => (
                      <DocumentCard
                        key={req.required_document_id}
                        req={req}
                        index={index}
                        onView={(doc) => doc.template_file_path && setViewingDoc(doc)}
                        isAdmin={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Off-Campus Activity Requirements — shown ONLY for Off-Campus users */}
            {isOffCampusUser && (
              <div className="bg-white rounded-[16px] sm:rounded-[20px] shadow-sm border border-gray-200 p-4 sm:p-6 overflow-hidden">
                <div className="flex items-start sm:items-center justify-between gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-gray-900 font-bold text-base sm:text-lg">
                      Off-Campus Activity Requirements
                    </h3>
                    <p className="text-gray-500 text-xs mt-0.5">
                      Required documents for off-campus activity compliance
                    </p>
                  </div>
                  <span className="bg-[#086C53]/10 text-[#086C53] text-[11px] sm:text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap flex-shrink-0 self-start sm:self-auto">
                    {offCampusReqs.length} Documents
                  </span>
                </div>

                {offCampusReqs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-12">
                    No off-campus activity requirements posted yet.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {offCampusReqs.map((req, index) => (
                      <DocumentCard
                        key={req.required_document_id}
                        req={req}
                        index={index}
                        onView={(doc) => doc.template_file_path && setViewingDoc(doc)}
                        isAdmin={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Document Preview Modal */}
      {viewingDoc &&
        createPortal(
          <div className="fixed top-0 left-0 w-screen h-screen bg-black/40 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
            <div
              className="bg-[#e5e7eb] rounded-[24px] w-full max-w-4xl h-[80vh] shadow-2xl overflow-hidden flex flex-col relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
                <div className="flex items-center space-x-3">
                  <div className="p-1.5 bg-gray-100 rounded text-gray-500">
                    <Icons.ClipboardList />
                  </div>
                  <h3 className="font-bold text-gray-800 text-lg">
                    {viewingDoc.document_name}
                  </h3>
                </div>
                <button
                  onClick={() => setViewingDoc(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2"
                >
                  <Icons.Close />
                </button>
              </div>
              <div className="flex-1 overflow-hidden p-4 custom-scrollbar relative">
                <div className="w-full h-full">
                  <FileViewer
                    fileUrl={viewingDoc.template_file_path}
                    fileName={viewingDoc.document_name}
                    filePath={viewingDoc.template_file_path}
                  />
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default OrgRequirements;